import React, { useState } from 'react';
import {
  ChevronRight, Check, Star, Award, Briefcase, Users,
  FileText, BookOpen, Sparkles, MapPin, Bookmark, ArrowRight, Info,
} from '../../components/CustomIcons';
import { Btn, Chip, Mono, ProgressBar, ScreenHeader, SafeImage } from './primitives';
import { MODULES, LESSONS_BY_MODULE, EMPLOYERS_BY_MODULE } from './learnerData';
import {
  EngagementLayer, JournalLink, SettingsButton, GoalBadge,
  MoodCheckin, ReflectionInput,
} from './engagement';

/* ============================================================
   Learner Flow Screens (1A → 3D)
   ============================================================ */

const IntakeQuestion = ({ label, name, options, value, onChange }) => (
  <fieldset className="space-y-3 border-0 p-0" style={{ minInlineSize: 0 }}>
    <legend className="text-[15px] font-semibold text-[#0f2744]">{label}</legend>
    <div className="grid grid-cols-1 gap-2" role="radiogroup" aria-label={label}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt)}
            data-testid={`proto-intake-${name}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
            className={`text-left px-4 py-3 rounded-[12px] border transition-all text-[15px] min-h-[48px] ${
              active
                ? 'p-card-active text-[#0f2744] font-semibold border-[#e2e8f0]'
                : 'border-[#e2e8f0] bg-[#f1f5f9] text-[#1c1c1e] hover:bg-white hover:border-[#cbd5e1]'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </fieldset>
);

export const Screen1A_Intake = ({ state, update }) => {
  const [local, setLocal] = useState(state.intake);
  const canSubmit = local.education && local.career && local.enrolled;
  return (
    <>
      <ScreenHeader title="Choose Your Path" subtitle="Onboarding" />
      <main className="px-6 py-5 space-y-6 overflow-y-auto flex-1">
        <section
          className="rounded-[12px] p-5"
          style={{ background: 'linear-gradient(140deg, #0f2744 0%, #081827 100%)', color: '#fafaf9' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" style={{ color: '#f97316' }} aria-hidden="true" />
            <span className="text-[12px] font-semibold tracking-widest uppercase" style={{ color: '#f97316' }}>Personalized Setup</span>
          </div>
          <h2 className="p-display text-[22px] leading-snug" style={{ color: '#fafaf9', margin: 0 }}>
            Answer three questions to match with your learning pathway.
          </h2>
        </section>
        <IntakeQuestion label="Select your current education level" name="education" value={local.education} onChange={(v) => setLocal((s) => ({ ...s, education: v }))} options={['High school', 'Some college', 'Trade certification', 'Other']} />
        <IntakeQuestion label="Select your career path" name="career" value={local.career} onChange={(v) => setLocal((s) => ({ ...s, career: v }))} options={['Technology & IT', 'Healthcare', 'Skilled Trades', 'Business & Finance', 'Other']} />
        <IntakeQuestion label="Are you currently enrolled in a facility-based program?" name="enrolled" value={local.enrolled} onChange={(v) => setLocal((s) => ({ ...s, enrolled: v }))} options={['Yes', 'No']} />
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
        <Btn
          onClick={() => update({ intake: local, step: '1B' })}
          disabled={!canSubmit}
          className={`w-full ${!canSubmit ? 'opacity-40 pointer-events-none' : ''}`}
          data-testid="proto-intake-submit"
          aria-label={canSubmit ? 'See recommended modules' : 'Select all options to continue'}
        >
          See Recommended Modules <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Btn>
      </footer>
    </>
  );
};

export const Screen1B_Modules = ({ state, update, onOpenChallenge, onOpenSettings, onOpenJournal }) => {
  return (
    <>
      <ScreenHeader title="Recommended Modules" subtitle="Onboarding" onBack={() => update({ step: '1A' })} />
      <main className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[15px] text-[#1c1c1e] leading-relaxed flex-1">Each pathway ends in a nationally recognized credential.</p>
          <div className="flex items-center gap-1 shrink-0">
            <JournalLink onOpen={onOpenJournal} />
            <SettingsButton onOpen={onOpenSettings} />
          </div>
        </div>
        <GoalBadge />
        <EngagementLayer onOpenChallenge={onOpenChallenge} />
        <div className="p-stagger space-y-4">
          {MODULES.map((m) => {
            const done = (state.completedLessons[m.id] || []).length;
            return (
              <button
                key={m.id}
                onClick={() => {
                  try {
                    const hasGoal = localStorage.getItem('assistivos_learner_goal');
                    if (!hasGoal) {
                      update({ moduleId: m.id, step: '1C', currentLessonIdx: 0, answerFeedback: null });
                      return;
                    }
                  } catch (_e) { /* ignore */ }
                  update({ moduleId: m.id, step: '2A', currentLessonIdx: 0, answerFeedback: null });
                }}
                data-testid={`proto-module-${m.id}`}
                className="w-full text-left rounded-[12px] bg-[#f1f5f9] hover:bg-white border border-[#e2e8f0] hover:border-[#0f2744] overflow-hidden transition-all group"
                aria-label={`${m.title}, ${m.hours} hours, credential ${m.credential}`}
              >
                <div className="h-36 w-full relative overflow-hidden">
                  <SafeImage src={m.image} alt={`${m.title} — ${m.field} illustration`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent 60%)' }} />
                  <div className="absolute bottom-3 left-5 right-5 flex items-end justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-white/85 text-[11px] font-semibold tracking-widest uppercase">{m.field}</div>
                      <h2 className="p-display text-[24px] leading-tight truncate" style={{ color: '#ffffff', margin: 0 }}>{m.title}</h2>
                    </div>
                    {done > 0 && (
                      <span className="rounded-full text-[11px] font-bold px-2.5 py-1 whitespace-nowrap" style={{ background: '#f97316', color: '#0f2744' }}>
                        In progress
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex flex-wrap gap-2 items-center">
                    <Chip mono><Mono>{m.hours}</Mono>&nbsp;hrs</Chip>
                    <Chip><Award className="w-3 h-3" aria-hidden="true" /> {m.credential}</Chip>
                    <Chip>~<Mono>8</Mono> min/lesson</Chip>
                  </div>
                  <p className="text-[15px] text-[#1c1c1e] leading-relaxed">{m.tagline}</p>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[13px] font-semibold text-[#0f2744]">Explore pathway</span>
                    <ChevronRight className="w-4 h-4 text-[#0f2744]" aria-hidden="true" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>
    </>
  );
};

export const Screen2A_ModuleOverview = ({ state, update }) => {
  const m = MODULES.find((x) => x.id === state.moduleId);
  const lessons = LESSONS_BY_MODULE[m.id];
  const completed = state.completedLessons[m.id] || [];
  return (
    <>
      <ScreenHeader
        title={m.title}
        subtitle="Module Overview"
        onBack={() => update({ step: '1B' })}
        breadcrumbs={[
          { label: 'Modules', onClick: () => update({ step: '1B' }) },
          { label: 'Module Overview' },
        ]}
      />
      <main className="overflow-y-auto flex-1">
        <div className="h-40 w-full relative">
          <SafeImage src={m.image} alt={`${m.title} — ${m.field} illustration`} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(15,39,68,0.9) 10%, rgba(15,39,68,0.35) 60%, transparent)' }} />
          <div className="absolute bottom-4 left-10 right-6">
            <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest" style={{ background: '#f97316', color: '#0f2744' }}>
              <Award className="w-3 h-3" aria-hidden="true" /> {m.credential}
            </div>
          </div>
        </div>
        <div className="pl-10 pr-6 py-5 space-y-6">
          <section className="grid grid-cols-2 gap-3" aria-label="Module summary">
            <div className="rounded-[12px] bg-[#f1f5f9] p-4">
              <div className="text-[12px] text-[#64748b] font-semibold uppercase tracking-wider">Lessons</div>
              <div className="text-[26px] leading-tight text-[#0f2744]"><Mono>{lessons.length}</Mono><span className="text-[13px] text-[#64748b] font-medium"> total</span></div>
            </div>
            <div className="rounded-[12px] bg-[#f1f5f9] p-4">
              <div className="text-[12px] text-[#64748b] font-semibold uppercase tracking-wider">Clock hours</div>
              <div className="text-[26px] leading-tight text-[#0f2744]"><Mono>{m.hours}</Mono><span className="text-[13px] text-[#64748b] font-medium"> hrs</span></div>
            </div>
          </section>
          <div className="rounded-[12px] bg-[#f1f5f9] p-4 flex items-start gap-3">
            <Info className="w-4 h-4 text-[#0f2744] mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-[13px] text-[#0f2744] leading-relaxed">Workforce Pell–eligible. Aligned with <Mono>38</Mono> HDGU partner employers across North Central Texas.</p>
          </div>
          <section aria-label="Curriculum">
            <h2 className="text-[12px] font-bold text-[#64748b] uppercase tracking-widest mb-2">Curriculum</h2>
            <ol className="space-y-2 list-none p-0">
              {lessons.map((l, i) => {
                const done = completed.includes(i);
                return (
                  <li key={i} className="flex items-center gap-3 rounded-[12px] bg-[#f1f5f9] px-4 py-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold ${done ? 'text-[#fafaf9]' : 'text-[#64748b]'}`}
                      style={{ background: done ? '#15803d' : '#e2e8f0' }}
                      aria-hidden="true"
                    >
                      {done ? <Check className="w-4 h-4" /> : i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-semibold text-[#0f2744] truncate">{l.title}</div>
                    </div>
                    {done && <span className="sr-only">Completed</span>}
                  </li>
                );
              })}
            </ol>
          </section>
          <MoodCheckin moduleId={m.id} />
        </div>
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
        <Btn onClick={() => update({ step: '2B', currentLessonIdx: 0, answerFeedback: null })} className="w-full" data-testid="proto-start-module">
          {completed.length > 0 ? 'Continue Module' : 'Start Module'} <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Btn>
      </footer>
    </>
  );
};

export const Screen2B_Lesson = ({ state, update }) => {
  const m = MODULES.find((x) => x.id === state.moduleId);
  const lessons = LESSONS_BY_MODULE[m.id];
  const idx = state.currentLessonIdx;
  const lesson = lessons[idx];
  const totalLessons = lessons.length;
  const progressPct = Math.round(((idx) / totalLessons) * 100);

  return (
    <>
      <ScreenHeader
        title={`Lesson ${idx + 1}: ${lesson.title.replace(/^Lesson \d+:\s*/, '')}`}
        subtitle={m.title}
        onBack={() => update({ step: '2A' })}
        breadcrumbs={[
          { label: 'Modules', onClick: () => update({ step: '1B' }) },
          { label: m.title, onClick: () => update({ step: '2A' }) },
          { label: `Lesson ${idx + 1}` },
        ]}
      />
      <main className="overflow-y-auto flex-1">
        <div className="pl-10 pr-6 pt-4">
          <ProgressBar value={progressPct} ariaLabel={`Lesson ${idx + 1} of ${totalLessons}, ${progressPct}% complete`} />
          <div className="mt-2 flex items-center justify-between text-[13px]">
            <span className="text-[#64748b] font-semibold">Lesson <Mono>{idx + 1}</Mono> of <Mono>{totalLessons}</Mono></span>
            <span className="text-[#0f2744] font-semibold"><Mono>{progressPct}%</Mono> complete</span>
          </div>
        </div>
        <article className="pl-10 pr-6 pt-4 pb-6 space-y-4 p-lesson-body">
          <div className="rounded-[12px] overflow-hidden aspect-[16/9] p-lesson-image">
            <SafeImage src={lesson.image} alt={`${lesson.title} illustration`} fallbackIcon={lesson.icon} className="w-full h-full object-cover" />
          </div>
          <p className="text-[17px] text-[#1c1c1e] leading-[1.55] p-lesson-text" style={{ maxWidth: '65ch' }}>{lesson.body}</p>
          <section className="rounded-[12px] bg-[#f1f5f9] p-4 space-y-2 p-lesson-bullets" aria-label="Key takeaways">
            <h3 className="text-[12px] font-bold text-[#0f2744] uppercase tracking-widest">Key Takeaways</h3>
            <ul className="space-y-2 list-none p-0">
              {lesson.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-[15px] text-[#0f2744]">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: '#f97316' }} aria-hidden="true" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </section>
        </article>
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
        <Btn onClick={() => update({ step: '2E' })} className="w-full" data-testid="proto-lesson-check">
          Take Knowledge Check <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Btn>
      </footer>
    </>
  );
};

export const Screen2E_KnowledgeCheck = ({ state, update }) => {
  const m = MODULES.find((x) => x.id === state.moduleId);
  const lessons = LESSONS_BY_MODULE[m.id];
  const idx = state.currentLessonIdx;
  const lesson = lessons[idx];
  const feedback = state.answerFeedback;

  const choose = (i) => {
    if (feedback) return;
    const correct = i === lesson.correctIndex;
    update((s) => {
      const already = (s.completedLessons[m.id] || []).includes(idx);
      const addHours = correct && !already ? 5 : 0;
      return {
        answerFeedback: { correct, chosenIdx: i },
        hoursByModule: { ...s.hoursByModule, [m.id]: (s.hoursByModule[m.id] || 0) + addHours },
        completedLessons: correct && !already
          ? { ...s.completedLessons, [m.id]: [...(s.completedLessons[m.id] || []), idx] }
          : s.completedLessons,
      };
    });
  };

  const proceed = () => {
    const isLast = idx >= lessons.length - 1;
    if (isLast) {
      update({ step: '2F', answerFeedback: null });
    } else {
      update({ step: '2B', currentLessonIdx: idx + 1, answerFeedback: null });
    }
  };

  return (
    <>
      <ScreenHeader
        title="Knowledge Check"
        subtitle={`Lesson ${idx + 1}`}
        onBack={() => update({ step: '2B', answerFeedback: null })}
        breadcrumbs={[
          { label: 'Modules', onClick: () => update({ step: '1B', answerFeedback: null }) },
          { label: m.title, onClick: () => update({ step: '2A', answerFeedback: null }) },
          { label: `Lesson ${idx + 1}`, onClick: () => update({ step: '2B', answerFeedback: null }) },
          { label: 'Check' },
        ]}
      />
      <main className="overflow-y-auto flex-1 pl-10 pr-6 py-5 space-y-4">
        <p className="text-[17px] font-semibold text-[#0f2744] leading-snug" id="proto-question" style={{ maxWidth: '65ch' }}>{lesson.question}</p>
        <div className="space-y-2.5" role="radiogroup" aria-labelledby="proto-question">
          {lesson.options.map((opt, i) => {
            const chosen = feedback?.chosenIdx === i;
            const isCorrect = i === lesson.correctIndex;
            let cls = 'border-[#e2e8f0] bg-[#f1f5f9] text-[#1c1c1e] hover:bg-white hover:border-[#cbd5e1]';
            if (feedback) {
              if (isCorrect) cls = 'text-[#0f2744] font-semibold';
              else if (chosen) cls = 'text-[#7f1d1d] font-semibold';
              else cls = 'text-[#94a3b8]';
            }
            const inlineStyle = {};
            if (feedback && isCorrect) { inlineStyle.background = 'rgba(21, 128, 61, 0.10)'; inlineStyle.borderColor = '#15803d'; }
            else if (feedback && chosen) { inlineStyle.background = 'rgba(220, 38, 38, 0.08)'; inlineStyle.borderColor = '#dc2626'; }
            return (
              <button
                key={i}
                role="radio"
                aria-checked={chosen}
                disabled={!!feedback}
                onClick={() => choose(i)}
                data-testid={`proto-answer-${i}`}
                className={`w-full text-left px-4 py-3.5 rounded-[12px] border transition-all text-[15px] flex items-center justify-between gap-3 min-h-[48px] ${cls}`}
                style={inlineStyle}
              >
                <span>{opt}</span>
                {feedback && isCorrect && <Check className="w-5 h-5 shrink-0" style={{ color: '#15803d' }} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        {feedback && (
          <div
            className="rounded-[12px] p-4"
            style={{
              background: feedback.correct ? 'rgba(21,128,61,0.08)' : 'rgba(245,158,11,0.10)',
              border: `1px solid ${feedback.correct ? '#15803d' : '#f59e0b'}`,
            }}
            role="status"
            aria-live="polite"
          >
            {feedback.correct ? (
              <>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#15803d' }}>
                    <Check className="w-4 h-4 text-white" aria-hidden="true" />
                  </div>
                  <span className="font-bold text-[15px]" style={{ color: '#15803d' }}>Correct.</span>
                </div>
                <p className="text-[14px] leading-relaxed" style={{ color: '#166534' }}><Mono>5</Mono> hours added toward your <strong>{m.credential}</strong>.</p>
              </>
            ) : (
              <>
                <div className="font-bold text-[15px] mb-1" style={{ color: '#92400e' }}>Incorrect.</div>
                <p className="text-[14px] leading-relaxed" style={{ color: '#78350f' }}>
                  The correct answer is <strong>{lesson.options[lesson.correctIndex]}</strong>. Review the lesson before continuing.
                </p>
              </>
            )}
          </div>
        )}
        {feedback?.correct && (
          <div
            className="rounded-[12px] p-5 text-center relative overflow-hidden p-badge-in"
            style={{ background: 'linear-gradient(140deg, #0f2744 0%, #081827 100%)', color: '#fafaf9' }}
            role="status"
            aria-live="polite"
          >
            <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: '#f97316' }} aria-hidden="true">
              <Star className="w-7 h-7" style={{ color: '#0f2744', fill: '#0f2744' }} />
            </div>
            <div className="p-display text-[18px]" style={{ color: '#fafaf9', margin: 0 }}>Lesson {idx + 1} Complete</div>
            <div className="text-[13px] mt-1" style={{ color: 'rgba(250,250,249,0.75)' }}>
              <Mono>{(state.hoursByModule[m.id] || 0)}</Mono> of <Mono>{m.hours}</Mono> hours completed
            </div>
          </div>
        )}
        {feedback?.correct && (
          <ReflectionInput moduleId={m.id} lessonIdx={idx} lessonTitle={lesson.title} />
        )}
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
        <Btn
          onClick={proceed}
          className={`w-full ${!feedback ? 'opacity-40 pointer-events-none' : ''}`}
          data-testid="proto-answer-continue"
          aria-disabled={!feedback}
        >
          {idx >= lessons.length - 1 ? 'View Credential Progress' : 'Continue to Next Lesson'} <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Btn>
      </footer>
    </>
  );
};

export const Screen2F_CredentialProgress = ({ state, update }) => {
  const m = MODULES.find((x) => x.id === state.moduleId);
  const lessons = LESSONS_BY_MODULE[m.id];
  const completed = state.completedLessons[m.id] || [];
  const hours = state.hoursByModule[m.id] || 0;
  const pct = Math.round((hours / m.hours) * 100);
  const eta = new Date();
  eta.setDate(eta.getDate() + 42 + Math.round((1 - pct / 100) * 14));
  const etaStr = eta.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <>
      <ScreenHeader
        title="Credential Progress"
        subtitle={m.credential}
        onBack={() => update({ step: '2A' })}
        breadcrumbs={[
          { label: 'Modules', onClick: () => update({ step: '1B' }) },
          { label: m.title, onClick: () => update({ step: '2A' }) },
          { label: 'Progress' },
        ]}
      />
      <main className="overflow-y-auto flex-1 pl-10 pr-6 py-5 space-y-5">
        <section
          className="rounded-[12px] p-5 relative overflow-hidden"
          style={{ background: 'linear-gradient(140deg, #0f2744 0%, #081827 100%)', color: '#fafaf9' }}
          aria-label="Credential status"
        >
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full blur-2xl" style={{ background: 'rgba(249,115,22,0.20)' }} />
          <div className="relative">
            <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#f97316' }}>On track to earn</div>
            <div className="p-display text-[24px] leading-tight mt-1" style={{ color: '#fafaf9', margin: 0 }}>{m.credential}</div>
            <div className="mt-5 flex items-end gap-2">
              <div className="text-[44px] leading-none"><Mono>{hours}</Mono></div>
              <div className="text-[15px] pb-1" style={{ color: 'rgba(250,250,249,0.7)' }}>of <Mono>{m.hours}</Mono> hours</div>
            </div>
            <div className="mt-3">
              <ProgressBar value={pct} ariaLabel={`${hours} of ${m.hours} hours toward ${m.credential}`} />
            </div>
            <div className="mt-3 flex items-center justify-between text-[13px]">
              <span style={{ color: 'rgba(250,250,249,0.8)' }}>Estimated completion</span>
              <span className="font-semibold" style={{ fontFamily: 'var(--p-font-mono)' }}>{etaStr}</span>
            </div>
          </div>
        </section>
        <section aria-label="Lesson checklist">
          <h2 className="text-[12px] font-bold text-[#64748b] uppercase tracking-widest mb-2">Completed lessons</h2>
          <ol className="space-y-2 list-none p-0">
            {lessons.map((l, i) => {
              const done = completed.includes(i);
              return (
                <li key={i} className="flex items-center gap-3 rounded-[12px] px-4 py-3" style={{ background: done ? 'rgba(21,128,61,0.08)' : '#f1f5f9' }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: done ? '#15803d' : '#e2e8f0', color: done ? '#fafaf9' : '#64748b' }} aria-hidden="true">
                    {done ? <Check className="w-4 h-4" /> : <span className="text-[12px] font-bold">{i + 1}</span>}
                  </div>
                  <div className="flex-1 min-w-0 text-[15px] font-semibold text-[#0f2744] truncate">{l.title}</div>
                  {done && <span className="sr-only">Completed</span>}
                </li>
              );
            })}
          </ol>
        </section>
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
        <Btn onClick={() => update({ step: '3A' })} className="w-full" data-testid="proto-view-employers">
          View Matched Employers <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Btn>
      </footer>
    </>
  );
};

export const Screen3A_Employers = ({ state, update }) => {
  const employers = EMPLOYERS_BY_MODULE[state.moduleId];
  const m = MODULES.find((x) => x.id === state.moduleId);
  return (
    <>
      <ScreenHeader
        title="Employers in Your Field"
        subtitle={m.title}
        onBack={() => update({ step: '2F' })}
        breadcrumbs={[
          { label: 'Modules', onClick: () => update({ step: '1B' }) },
          { label: m.title, onClick: () => update({ step: '2A' }) },
          { label: 'Employers' },
        ]}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5">
        <div className="p-wipe space-y-3">
          {employers.map((e) => (
            <button
              key={e.id}
              onClick={() => update({ employerId: e.id, step: '3B' })}
              data-testid={`proto-employer-${e.id}`}
              className="w-full text-left rounded-[12px] bg-[#f1f5f9] hover:bg-white border border-[#e2e8f0] hover:border-[#0f2744] p-5 transition-all group"
              aria-label={`${e.name} in ${e.location}. ${e.salary}. ${e.openings} openings.`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-[12px] text-white flex items-center justify-center font-bold text-[15px] shrink-0" style={{ background: '#0f2744', color: '#fafaf9', fontFamily: 'var(--p-font-mono)' }}>
                      {e.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                    </div>
                    <div className="min-w-0">
                      <h2 className="p-display text-[19px] leading-tight truncate" style={{ margin: 0 }}>{e.name}</h2>
                      <div className="text-[13px] text-[#64748b] flex items-center gap-1"><MapPin className="w-3 h-3" aria-hidden="true" /> {e.location}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                    <Chip mono>{e.salary}</Chip>
                    <Chip><Briefcase className="w-3 h-3" aria-hidden="true" /><Mono>{e.openings}</Mono> openings</Chip>
                  </div>
                  <p className="text-[14px] text-[#1c1c1e] mt-2 leading-relaxed">{e.field}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-[#94a3b8] group-hover:text-[#0f2744] shrink-0 mt-1" aria-hidden="true" />
              </div>
            </button>
          ))}
        </div>
      </main>
    </>
  );
};

export const Screen3B_EmployerDetail = ({ state, update }) => {
  const employers = EMPLOYERS_BY_MODULE[state.moduleId];
  const e = employers.find((x) => x.id === state.employerId);
  const saved = state.savedEmployers.includes(e.id);
  const toggleSave = () =>
    update((s) => ({
      savedEmployers: saved ? s.savedEmployers.filter((x) => x !== e.id) : [...s.savedEmployers, e.id],
    }));

  return (
    <>
      <ScreenHeader
        title={e.name}
        subtitle="Open Position"
        onBack={() => update({ step: '3A' })}
        breadcrumbs={[
          { label: 'Modules', onClick: () => update({ step: '1B' }) },
          { label: 'Employers', onClick: () => update({ step: '3A' }) },
          { label: e.name },
        ]}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <section
          className="rounded-[12px] p-5"
          style={{ background: 'linear-gradient(140deg, #0f2744 0%, #081827 100%)', color: '#fafaf9' }}
          aria-label="Position summary"
        >
          <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#f97316' }}>Open role</div>
          <h2 className="p-display text-[22px] leading-tight mt-1" style={{ color: '#fafaf9', margin: 0 }}>{e.title}</h2>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: 'rgba(250,250,249,0.6)' }}>Median entry wage</div>
              <div className="text-[16px] mt-0.5"><Mono>{e.salary}</Mono></div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: 'rgba(250,250,249,0.6)' }}>Openings</div>
              <div className="text-[16px] mt-0.5"><Mono>{e.openings}</Mono></div>
            </div>
          </div>
        </section>
        <section aria-label="Job responsibilities">
          <h3 className="text-[12px] font-bold text-[#64748b] uppercase tracking-widest mb-2">Responsibilities</h3>
          <ul className="space-y-2 list-none p-0">
            {e.responsibilities.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-[15px] text-[#1c1c1e]">
                <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: '#f97316' }} aria-hidden="true" />
                <span className="leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>
        </section>
        <div className="rounded-[12px] p-4" style={{ background: 'rgba(21,128,61,0.08)', border: '1px solid #15803d' }}>
          <div className="flex items-start gap-2.5">
            <Award className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#15803d' }} aria-hidden="true" />
            <div>
              <div className="text-[14px] font-bold" style={{ color: '#15803d' }}>Required: {e.credential}</div>
              <div className="text-[13px] mt-0.5 leading-relaxed" style={{ color: '#166534' }}>On track through your current pathway.</div>
            </div>
          </div>
        </div>
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9] grid grid-cols-2 gap-2">
        <Btn variant="ghost" onClick={toggleSave} data-testid="proto-employer-save" aria-pressed={saved}>
          <Bookmark className={`w-4 h-4 ${saved ? 'fill-current' : ''}`} aria-hidden="true" /> {saved ? 'Saved' : 'Save Employer'}
        </Btn>
        <Btn onClick={() => update({ step: '3C' })} data-testid="proto-employer-continue">
          View Position <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Btn>
      </footer>
    </>
  );
};

export const Screen3C_Toolkit = ({ update }) => {
  const items = [
    { icon: FileText, title: 'Resume Builder', desc: 'Templates and industry-specific guidance.', tag: 'Ready' },
    { icon: Users, title: 'Interview Prep', desc: 'Practice scenarios, scripts, and common questions.', tag: 'Ready' },
    { icon: Briefcase, title: 'Job Board Directory', desc: 'Indeed, LinkedIn, WorkInTexas.com.', tag: 'Ready' },
    { icon: BookOpen, title: 'Mentorship Network', desc: 'Connect with working professionals in your field.', tag: 'Coming soon' },
  ];
  return (
    <>
      <ScreenHeader
        title="Post-Release Toolkit"
        subtitle="Resources"
        onBack={() => update({ step: '3B' })}
        breadcrumbs={[
          { label: 'Modules', onClick: () => update({ step: '1B' }) },
          { label: 'Employers', onClick: () => update({ step: '3A' }) },
          { label: 'Toolkit' },
        ]}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
        {items.map((it, i) => (
          <section key={i} className="rounded-[12px] bg-[#f1f5f9] p-4 flex items-start gap-3" aria-label={it.title}>
            <div className="w-11 h-11 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: '#e2e8f0', color: '#0f2744' }} aria-hidden="true">
              <it.icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="p-display text-[17px] leading-tight" style={{ margin: 0 }}>{it.title}</h3>
                <span
                  className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={it.tag === 'Ready'
                    ? { background: 'rgba(21,128,61,0.12)', color: '#15803d' }
                    : { background: '#e2e8f0', color: '#64748b' }}
                >
                  {it.tag}
                </span>
              </div>
              <p className="text-[14px] text-[#1c1c1e] mt-1 leading-relaxed">{it.desc}</p>
            </div>
          </section>
        ))}
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
        <Btn onClick={() => update({ step: '3D' })} className="w-full" data-testid="proto-toolkit-continue">
          Complete Overview <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Btn>
      </footer>
    </>
  );
};

export const Screen3D_Completion = ({ state, update, reset }) => {
  const m = MODULES.find((x) => x.id === state.moduleId);
  const hours = state.hoursByModule[m.id] || 0;
  return (
    <>
      <ScreenHeader
        title="Module Complete"
        subtitle={m.title}
        onBack={() => update({ step: '3C' })}
        breadcrumbs={[
          { label: 'Modules', onClick: () => update({ step: '1B' }) },
          { label: 'Complete' },
        ]}
      />
      <main className="overflow-y-auto flex-1 px-6 py-6 space-y-5">
        <section
          className="rounded-[12px] p-6 text-center relative overflow-hidden"
          style={{ background: 'linear-gradient(140deg, #0f2744 0%, #081827 100%)', color: '#fafaf9' }}
          aria-label="Completion summary"
        >
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full blur-3xl" style={{ background: 'rgba(249,115,22,0.25)' }} />
          <div className="relative">
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 p-badge-in" style={{ background: '#f97316' }} aria-hidden="true">
              <Award className="w-8 h-8" style={{ color: '#0f2744' }} />
            </div>
            <div className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: '#f97316' }}>You&apos;re on your way</div>
            <div className="p-display text-[22px] leading-snug" style={{ color: '#fafaf9', margin: 0 }}>Lessons complete in {m.title}.</div>
            <p className="text-[14px] mt-2 leading-relaxed" style={{ color: 'rgba(250,250,249,0.85)' }}>
              <Mono>{hours}</Mono> hours logged toward <strong style={{ color: '#fafaf9' }}>{m.credential}</strong>.
            </p>
          </div>
        </section>
        <section className="rounded-[12px] bg-[#f1f5f9] p-4 text-center" aria-label="What's next">
          <h3 className="text-[12px] font-bold text-[#64748b] uppercase tracking-widest">What&apos;s next</h3>
          <p className="text-[14px] text-[#1c1c1e] mt-1.5 leading-relaxed">
            Continue toward a credential recognized by <Mono>38</Mono> HDGU partner employers across North Central Texas.
          </p>
        </section>
      </main>
      <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9] space-y-2">
        <Btn onClick={() => update({ step: '1B', employerId: null, answerFeedback: null })} className="w-full" data-testid="proto-explore-another">
          Explore Another Module <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Btn>
        <button
          onClick={() => { if (window.confirm('Reset the entire demo? All progress will be cleared.')) reset(); }}
          className="w-full text-[13px] text-[#64748b] hover:text-[#0f2744] font-semibold py-2 rounded-[8px]"
          data-testid="proto-reset"
        >
          Reset demo
        </button>
      </footer>
    </>
  );
};
